import type { ReactNode, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

import {
  Box,
  Button,
  Collapse,
  IconButton,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ChevronDown } from 'lucide-react';

import { CountBadge } from '@modules/_shared/components/CountBadge';
import { useLocale } from '@shared/hooks/locale';
import { TOP_BAR_HEIGHT } from '@shared/theme/layout';
import { borderRadius } from '@shared/theme/StyleProvider';

type DashboardSectionShellProps = {
  title: ReactNode;
  titleAdornment?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  count?: number;
  hideBadge?: boolean;
  loading?: boolean;
  expandable?: boolean;
  expanded?: boolean;
  hasMoreItems?: boolean;
  loadMoreRef?: RefObject<HTMLButtonElement | null>;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  bodySx?: SxProps<Theme>;
  anchorId?: string;
  highlighted?: boolean;
  children?: ReactNode;
};

export const DashboardSectionShell = ({
  title,
  titleAdornment,
  subtitle,
  action,
  count,
  hideBadge = false,
  loading = false,
  expandable = false,
  expanded: controlledExpanded,
  hasMoreItems = false,
  loadMoreRef,
  onLoadMore,
  isLoadingMore = false,
  bodySx,
  anchorId,
  highlighted = false,
  children,
}: DashboardSectionShellProps) => {
  const { t } = useLocale();
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(false);
  const isExpanded = controlledExpanded ?? uncontrolledExpanded;
  const didAutoExpandRef = useRef(false);

  useEffect(() => {
    if (
      expandable &&
      controlledExpanded == null &&
      !loading &&
      !didAutoExpandRef.current
    ) {
      didAutoExpandRef.current = true;
      setUncontrolledExpanded(true);
    }
  }, [expandable, controlledExpanded, loading]);

  const showBadge = !hideBadge && (count ?? 0) >= 1;

  const toggleExpanded = () => {
    if (controlledExpanded === undefined) {
      setUncontrolledExpanded(prev => !prev);
    }
  };

  return (
    <Paper
      id={anchorId}
      elevation={0}
      sx={theme => ({
        borderRadius: borderRadius.large,
        border: '1px solid',
        borderColor: highlighted ? 'primary.main' : 'grey.200',
        boxShadow: highlighted
          ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.25)}`
          : 'none',
        scrollMarginTop: `${TOP_BAR_HEIGHT + 16}px`,
        transition: 'border-color 0.4s ease, box-shadow 0.4s ease',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      })}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: { xs: 1.5, sm: 2 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          {loading ? (
            <Skeleton variant="text" width={160} height={24} />
          ) : (
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
          )}
          {!loading && showBadge && (
            <CountBadge count={count!} variant="alert" />
          )}
          {!loading && titleAdornment}
          {!loading && subtitle != null && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontWeight: 500, width: '100%' }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          {action}
          {expandable && (
            <IconButton
              size="small"
              onClick={toggleExpanded}
              aria-label={isExpanded ? t('Collapse') : t('Expand')}
              sx={{
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            >
              <ChevronDown size={18} />
            </IconButton>
          )}
        </Box>
      </Box>
      {children != null && (
        <Collapse in={!expandable || isExpanded} timeout="auto">
          <Box
            sx={[
              { px: { xs: 2, sm: 2.5 }, pt: 0, pb: { xs: 2, sm: 2.5 } },
              ...(Array.isArray(bodySx) ? bodySx : [bodySx ?? {}]),
            ]}
          >
            {children}
            {hasMoreItems && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: 0.5,
                }}
              >
                <Button
                  ref={loadMoreRef}
                  onClick={onLoadMore}
                  loading={isLoadingMore}
                  loadingPosition="start"
                  size="small"
                  sx={{
                    textTransform: 'none',
                    px: 1.5,
                    py: 0.25,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  {t('Load more')}
                </Button>
              </Box>
            )}
          </Box>
        </Collapse>
      )}
    </Paper>
  );
};
